import { etagsDiffer } from './parse';
import { deployCheckWorkerSource } from './worker-source';

export { etagsDiffer, parseEtag } from './parse';

/** Dispatched when uploaded index.html has a different data-etag than the running tab. */
export const DEPLOY_UPDATED_EVENT = 'multiplier:deploy-updated';

const RELOAD_GUARD_KEY = 'multiplier.deploy-check.reload';
const CHECK_INTERVAL_MS = 3_600_000;

export type CheckDeployVersionOptions = {
  etag: string;
};

const createBlobWorker = (): Worker => {
  const blob = new Blob([deployCheckWorkerSource], {
    type: 'text/javascript',
  });
  return new Worker(URL.createObjectURL(blob));
};

export const startCheckDeployVersion = ({
  etag,
}: CheckDeployVersionOptions): (() => void) => {
  if (typeof window === 'undefined' || !etag) {
    return () => undefined;
  }

  const indexUrl = `${window.location.origin}/index.html`;
  const worker = createBlobWorker();

  const requestCheck = () => {
    worker.postMessage({
      type: 'check',
      etag,
      indexUrl,
    });
  };

  const notifyOnce = (uploadedEtag: string) => {
    if (!etagsDiffer(etag, uploadedEtag)) return;
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) === uploadedEtag) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, uploadedEtag);
    window.dispatchEvent(new Event(DEPLOY_UPDATED_EVENT));
  };

  worker.onmessage = (event: MessageEvent) => {
    const uploadedEtag = event.data?.uploadedEtag as string | undefined;
    if (event.data?.type !== 'outdated' || !uploadedEtag) return;

    if (document.visibilityState === 'visible') {
      notifyOnce(uploadedEtag);
    }
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      requestCheck();
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  const intervalId = window.setInterval(requestCheck, CHECK_INTERVAL_MS);
  requestCheck();

  return () => {
    window.clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onVisibility);
    worker.terminate();
  };
};
